'use client';

import { TemplateCategory, ComplexityLevel, TemplateFilters as TFilters } from '@/types/template';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';
import { templateCategories } from '@/content/templates';

interface TemplateFiltersProps {
  filters: TFilters;
  onFiltersChange: (filters: TFilters) => void;
  totalCount: number;
}

export function TemplateFilters({ filters, onFiltersChange, totalCount }: TemplateFiltersProps) {
  const handleCategoryChange = (category: string) => {
    onFiltersChange({
      ...filters,
      category: category === 'all' ? undefined : (category as TemplateCategory),
    });
  };

  const handleSearchChange = (search: string) => {
    onFiltersChange({
      ...filters,
      search: search || undefined,
    });
  };

  const handleComplexityChange = (complexity: string) => {
    onFiltersChange({
      ...filters,
      complexity: complexity === 'all' ? undefined : (complexity as ComplexityLevel),
    });
  };

  const handleSortChange = (sortBy: string) => {
    onFiltersChange({
      ...filters,
      sortBy: sortBy as TFilters['sortBy'],
    });
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={filters.search || ''}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Category Tabs */}
      <div>
        <Tabs
          value={filters.category || 'all'}
          onValueChange={handleCategoryChange}
          className="w-full"
        >
          <TabsList className="w-full justify-start flex-wrap h-auto">
            <TabsTrigger value="all" className="flex-shrink-0">
              All Templates ({totalCount})
            </TabsTrigger>
            {templateCategories.map((category) => (
              <TabsTrigger
                key={category.value}
                value={category.value}
                className="flex-shrink-0"
              >
                {category.label} ({category.count})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Complexity Filter */}
        <Select
          value={filters.complexity || 'all'}
          onValueChange={handleComplexityChange}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Complexity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort Filter */}
        <Select
          value={filters.sortBy || 'newest'}
          onValueChange={handleSortChange}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="alphabetical">Alphabetical</SelectItem>
          </SelectContent>
        </Select>

        {/* Active Filters Count */}
        {(filters.category || filters.complexity || filters.search) && (
          <div className="flex items-center text-sm text-muted-foreground">
            <button
              onClick={() => onFiltersChange({})}
              className="hover:text-foreground transition-colors underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
